import dispatcher from "../dispatcher"
import { EPunchSetApprovalType, ITimecardSequenceResult, IDate, IPunchSetDocument, EExceptionType } from "../models"
import AccountStore from "../stores/AccountStore"
import AppStateStore from "../stores/AppStateStore"
import * as api from "../api"
import strings from "../localization/strings"
import * as utils from "../helpers/utils"
import * as accountActions from "./AccountActions"

export function approvePunchSet(punchSet: IPunchSetDocument, cb: (msg: string) => void) {
    AppStateStore.setProgressSnackbarContent("loading")
    api.approvePunchSet(AccountStore.getCurrentAccount().Id, punchSet.Id)
        .then(res => {
            if (res.data === "Punchset_approved_successfully") {
                cb ?
                    cb(strings.PunchSet_approved_successfully) :
                    AppStateStore.setProgressSnackbarContent(strings.PunchSet_approved_successfully)
            }
            else {
                if (res.data.includes("Blocking_timecard_validation_issues_exist")) {
                    AppStateStore.setProgressSnackbarContent(null)
                    AppStateStore.setAlertDialogContent({
                        title: strings.Cannot_Approve_PunchSet,
                        content: "Validation failed on one more items when approving the punchset",
                        subContent: "See timecard validation information for more details."
                    })
                }
                else {
                    AppStateStore.setProgressSnackbarContent(strings[res.data])
                }
            }
        })
        .catch(() => {
            AppStateStore.setProgressSnackbarContent(strings.Failed_to_approve_PunchSet)
        })
}

export function unApprovePunchSet(punchSet: IPunchSetDocument, cb: (msg: string) => void) {
    AppStateStore.setProgressSnackbarContent("loading")
    api.unApprovePunchSet(AccountStore.getCurrentAccount().Id, punchSet.Id)
        .then(res => {
            if (res.data === "Punchset_unapproved_successfully") {
                cb ?
                    cb(strings.PunchSet_unapproved_successfully) :
                    AppStateStore.setProgressSnackbarContent(strings.PunchSet_unapproved_successfully)
            }
            else {
                AppStateStore.setProgressSnackbarContent(strings[res.data])
            }
        })
        .catch(() => {
            AppStateStore.setProgressSnackbarContent(strings.Failed_to_unapprove_PunchSet)
        })
}

export async function handleApproveAllTimecards(accountId: string, referenceDate: IDate, hierarchyItemId: string, filterByDirectReports: boolean, filterByDefaultHierarchy: boolean, filterByException: boolean, payGroupIds: string[], filterExceptions: EExceptionType[], cb: (msg: string) => void) {
    AppStateStore.setProgressSnackbarContent("loading")
    const asyncSessionId = utils.guid()
    setTimeout(() =>
        dispatcher.dispatch({
            type: "UPDATE_ASYNC_SESSION_ID",
            data: asyncSessionId
        }), 0)

    try {
        const res = await api.approveAllTimecards(accountId, referenceDate, hierarchyItemId, filterByDirectReports, filterByDefaultHierarchy, filterByException, payGroupIds, filterExceptions, asyncSessionId)
        const result: ITimecardSequenceResult = res.data
        handleApproveUnapproveResponse(result, () => {
            accountActions.clearAsyncSessionId()
            handleApproveAllTimecards(accountId, referenceDate, hierarchyItemId, filterByDirectReports, filterByDefaultHierarchy, filterByException, payGroupIds, filterExceptions, cb)
        }, cb)
    }
    catch (e) {
        AppStateStore.setProgressSnackbarContent(strings.Failed_to_approve_Timecard)
    }
}

export async function handleUnapproveAllTimecards(accountId: string, referenceDate: IDate, hierarchyItemId: string, filterByDirectReports: boolean, filterByDefaultHierarchy: boolean, filterByException: boolean, payGroupIds: string[], filterExceptions: EExceptionType[], cb: (msg: string) => void) {
    AppStateStore.setProgressSnackbarContent("loading")
    const asyncSessionId = utils.guid()
    setTimeout(() => {
        dispatcher.dispatch({
            type: "UPDATE_ASYNC_SESSION_ID",
            data: asyncSessionId
        })
    }, 0)

    try {
        const res = await api.unapproveAllTimecards(accountId, referenceDate, hierarchyItemId, filterByDirectReports, filterByDefaultHierarchy, filterByException, payGroupIds, filterExceptions, asyncSessionId)
        const result: ITimecardSequenceResult = res.data
        handleApproveUnapproveResponse(result, () => {
            accountActions.clearAsyncSessionId()
            handleUnapproveAllTimecards(accountId, referenceDate, hierarchyItemId, filterByDirectReports, filterByDefaultHierarchy, filterByException, payGroupIds, filterExceptions, cb)
        }, cb)
    }
    catch (e) {
        AppStateStore.setProgressSnackbarContent(strings.Failed_to_unapprove_Timecard)
    }
}

export async function handleApproveTimecard(timecardIds: string[], jobId: string, dayOfWeekIndex: number, punchSetApprovalType: EPunchSetApprovalType, cb: (msg: string) => void) {
    AppStateStore.setProgressSnackbarContent("loading")
    try {
        const res = await api.approveTimecards(AccountStore.getCurrentAccount().Id, timecardIds, jobId, dayOfWeekIndex, punchSetApprovalType)
        const result: ITimecardSequenceResult = res.data
        handleApproveUnapproveResponse(result, () => handleApproveTimecard(timecardIds, jobId, dayOfWeekIndex, punchSetApprovalType, cb), cb)
    }
    catch (e) {
        AppStateStore.setProgressSnackbarContent(strings.Failed_to_approve_Timecard)
    }
}

export async function handleUnapproveTimecard(timecardIds: string[], jobId: string, dayOfWeekIndex: number, punchSetApprovalType: EPunchSetApprovalType, cb: (msg: string) => void) {
    AppStateStore.setProgressSnackbarContent("loading")
    try {
        const res = await api.unapproveTimecards(AccountStore.getCurrentAccount().Id, timecardIds, jobId, dayOfWeekIndex, punchSetApprovalType)
        const result: ITimecardSequenceResult = res.data
        handleApproveUnapproveResponse(result, () => handleUnapproveTimecard(timecardIds, jobId, dayOfWeekIndex, punchSetApprovalType, cb), cb)
    }
    catch (e) {
        AppStateStore.setProgressSnackbarContent(strings.Failed_to_unapprove_Timecard)
    }
}

function handleApproveUnapproveResponse(result: ITimecardSequenceResult, handleRetry: () => void, cb: (msg?: string) => void) {
    if (result.WarningData && result.WarningData.WarningItems.length > 0) {
        AppStateStore.setTimecardSequenceWarningDialogContent({
            warningData: result.WarningData,
            handleRetry: handleRetry
        })
    }
    else {
        AppStateStore.setProgressSnackbarContent(null)
        AppStateStore.setTextProgressSnackbarContent(null)
        if (result.Result === "Cannot_approve_the_timecard_which_has_opened_punchset") {
            AppStateStore.setAlertDialogContent({
                title: strings.CannotApproveTimecardsTitle,
                content: strings.Cannot_approve_the_timecard_which_has_opened_punchset
            })
        }
        else if (result.Result.includes("Blocking_timecard_validation_issues_exist")) {
            AppStateStore.setAlertDialogContent({
                title: strings.CannotApproveTimecardsTitle,
                content: "Validation failed on one more items when approving the timecard",
                subContent: "See timecard validation information for more details."
            })
            cb()
        }
        else if (result.Result.includes("Cannot_approve_the_locked_timecard")) {
            const employeesInfo = result.Result.replace("Cannot_approve_the_locked_timecard:", "")
            AppStateStore.setAlertDialogContent({
                title: strings.CannotApproveTimecardsTitle,
                content: strings.Cannot_approve_the_locked_timecard,
                subContent: employeesInfo.split("\n")
            })
        }
        else if (result.Result.includes("Cannot_unapprove_the_locked_timecard")) {
            const employeesInfo = result.Result.replace("Cannot_unapprove_the_locked_timecard:", "")
            AppStateStore.setAlertDialogContent({
                title: strings.Cannot_UnApprove_Timecard,
                content: strings.Cannot_unapprove_the_locked_timecard,
                subContent: employeesInfo.split("\n")
            })
        }
        else {
            cb(strings[result.Result])
        }
    }
}